import { forwardRef } from 'react';
import Upload from '../svgs/Upload';

type UploadFieldProps = {
  title: string;
  inputId: string;
  acceptedFileExtensions: string;
  maxFileSizeMB?: number;
  acceptedTypes?: string[];
} & React.ComponentProps<'input'>;

const UploadField = forwardRef<HTMLInputElement, UploadFieldProps>(
  (
    {
      title,
      acceptedFileExtensions,
      inputId,
      maxFileSizeMB,
      acceptedTypes,
      ...rest
    },
    ref,
  ) => {
    // Format accepted types for display (e.g., ["image/jpeg", "image/png"] -> "JPG or PNG")
    const formatAcceptedTypes = (types: string[]): string => {
      const typeMap: Record<string, string> = {
        'image/jpeg': 'JPG',
        'image/jpg': 'JPG',
        'image/png': 'PNG',
        'image/gif': 'GIF',
        'image/bmp': 'BMP',
      };

      const formatted = types
        .map((type) => typeMap[type.toLowerCase()])
        .filter((type) => type)
        .filter((value, index, self) => self.indexOf(value) === index); // Remove duplicates

      if (formatted.length === 0) return 'file';
      if (formatted.length === 1) return formatted[0];
      if (formatted.length === 2) return `${formatted[0]} or ${formatted[1]}`;
      return `${formatted.slice(0, -1).join(', ')}, or ${
        formatted[formatted.length - 1]
      }`;
    };

    const displayText =
      maxFileSizeMB && acceptedTypes
        ? `${formatAcceptedTypes(acceptedTypes)}, max ${maxFileSizeMB}MB`
        : title;

    return (
      <>
        <label
          className="flex h-full w-full cursor-pointer items-center justify-center"
          htmlFor={inputId}
        >
          <Upload className="mr-2 h-5 w-5 text-secondary-100" />
          <span>{displayText}</span>
        </label>
        <input
          ref={ref}
          id={inputId}
          type="file"
          className="hidden"
          accept={acceptedFileExtensions}
          {...rest}
        />
      </>
    );
  },
);

export default UploadField;
